/**
 * @fileoverview Media Query Hook
 * @description React hook exposing a CSS media query as reactive state, used to
 * branch drawer/overlay behaviour between mobile and desktop layouts
 */

import { useState, useEffect } from 'react';

/**
 * Custom hook tracking whether a CSS media query currently matches
 * @param query - Media query string, e.g. '(min-width: 1024px)'
 * @returns True while the query matches the current viewport
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(
    () => window.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Re-sync in case the viewport changed between render and effect
    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);

    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
};
